"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Pickaxe,
  Mountain,
  Users,
  Microscope,
  Shield,
  Leaf,
  Award,
  Clock,
  Phone,
  Mail,
  Gem,
  Star,
  RefreshCw,
  FileVideo2,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Footer from "./Footer";
import MiningProcessSection from "./Minning/MiningProcessSection";
import GallerySection from "./Minning/GallerySection";

import Image1 from "../Assets/portrait-engineers-work-hours-job-site.jpg?format=webp";
import Image2 from "../Assets/person-working-building-construction.jpg?format=webp";
import Image3 from "../Assets/closeup-shot-ongoing-construction-with-tracks-bulldozer-abandoned-land.jpg?format=webp";
import Image8 from "../Assets/portrait-man-practicing-his-profession-celebrate-international-labour-day-Y8jNubTq.jpg?format=webp";
import Image16 from "../Assets/0000412490_resized_kibaligoldminedrcoctober20141022.jpg?format=webp";

// Corrected video path;
import video3 from "../Assets/Dubai Panjshir EmeraldVault.mp4";
import Video1 from "../Assets/the-ediet-vidue.mp4";

// Animation variants with faster transitions
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

// Mining services data
const miningServices = [
  {
    id: 1,
    title: "Exploration & Surveying",
    description: "Advanced geological surveys to locate Panjshir emerald deposits.",
    icon: <Mountain className="h-10 w-10 text-emerald-300" />,
    details: [
      "Geological mapping of Panjshir Valley terrain",
      "Geochemical analysis of chromium-rich zones",
      "High-altitude satellite imagery interpretation",
      "Ground-penetrating radar for vein detection",
      "Core sampling tailored to Panjshir geology",
    ],
    image: Image2,
  },
  {
    id: 2,
    title: "Extraction Operations",
    description: "Sustainable mining in the Panjshir Valley to extract emeralds.",
    icon: <Pickaxe className="h-10 w-10 text-emerald-300" />,
    details: [
      "Shaft mining in rugged Panjshir landscapes",
      "Selective emerald extraction techniques",
      "Water conservation in arid valley conditions",
      "Low-impact blasting in sensitive areas",
      "Safety protocols for high-altitude operations",
    ],
    image: Image3,
  },
  {
    id: 3,
    title: "Processing & Sorting",
    description: "Expert evaluation and sorting of Panjshir rough emeralds.",
    icon: <Microscope className="h-10 w-10 text-emerald-300" />,
    details: [
      "Cleaning of Panjshir rough emeralds",
      "Sorting by chromium-driven color intensity",
      "Gemological grading of valley-specific traits",
      "Quality classification for cutting",
      "Preservation of natural Panjshir specimens",
    ],
    image: "/placeholder.svg?height=600&width=800",
  },
  {
    id: 4,
    title: "Mining Consulting",
    description: "Specialized consulting for Panjshir emerald mining stakeholders.",
    icon: <Users className="h-10 w-10 text-emerald-300" />,
    details: [
      "Panjshir mine valuation and feasibility",
      "Optimization of valley mining operations",
      "Compliance with Afghan environmental laws",
      "Market analysis for Panjshir emeralds",
      "Investment strategies for Panjshir projects",
    ],
    image: Image16,
  },
];

// Sustainability practices data
const sustainabilityPractices = [
  {
    id: 1,
    title: "Environmental Care",
    description: "Using water-efficient systems to protect the Panjshir Valley ecosystem.",
    icon: <Leaf className="h-10 w-10 text-emerald-300" />,
  },
  {
    id: 2,
    title: "Community Support",
    description: "Empowering Panjshir locals with education and healthcare.",
    icon: <Users className="h-10 w-10 text-emerald-300" />,
  },
  {
    id: 3,
    title: "Ethical Valley Practices",
    description: "Ensuring fair wages and safe conditions for Panjshir miners.",
    icon: <Shield className="h-10 w-10 text-emerald-300" />,
  },
  {
    id: 4,
    title: "Transparency",
    description: "Tracking every Panjshir emerald from valley to market.",
    icon: <Award className="h-10 w-10 text-emerald-300" />,
  },
];

export default function MiningPage() {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const sustainabilityRef = useRef(null);
  const stakeHoldingRef = useRef(null);
  const contactRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const servicesInView = useInView(servicesRef, { once: true, amount: 0.3 });
  const sustainabilityInView = useInView(sustainabilityRef, { once: true, amount: 0.3 });
  const stakeHoldingInView = useInView(stakeHoldingRef, { once: true, amount: 0.3 });
  const contactInView = useInView(contactRef, { once: true, amount: 0.3 });

  const heroControls = useAnimation();
  const servicesControls = useAnimation();
  const sustainabilityControls = useAnimation();
  const stakeHoldingControls = useAnimation();
  const contactControls = useAnimation();

  const [activeService, setActiveService] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (heroInView) heroControls.start("visible");
    if (servicesInView) servicesControls.start("visible");
    if (sustainabilityInView) sustainabilityControls.start("visible");
    if (stakeHoldingInView) stakeHoldingControls.start("visible");
    if (contactInView) contactControls.start("visible");
  }, [
    heroInView,
    servicesInView,
    sustainabilityInView,
    stakeHoldingInView,
    contactInView,
    heroControls,
    servicesControls,
    sustainabilityControls,
    stakeHoldingControls,
    contactControls,
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Email is invalid";
    }
    if (!formData.message.trim()) errors.message = "Message is required";
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      toast.error("Please fill out all required fields correctly", {
        position: "top-right",
        icon: <CheckCircle className="text-red-600" size={20} />,
      });
      return;
    }

    try {
      const message = `
Dubai Panjshir EmeraldVault Contact Form Submission:
Name: ${formData.name}
Email: ${formData.email}
Service: ${formData.service || "Not specified"}
Message: ${formData.message}
      `.trim();
      const encodedMessage = encodeURIComponent(message);
      const whatsappNumber = "+93729599599";
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

      window.open(whatsappUrl, "_blank");

      toast.success("Redirecting to WhatsApp with your message!", {
        position: "top-right",
        icon: <CheckCircle className="text-emerald-600" size={20} />,
      });

      setFormData({
        name: "",
        email: "",
        service: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to open WhatsApp. Please try again.", {
        position: "top-right",
        icon: <CheckCircle className="text-red-600" size={20} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-gray-50 to-white font-serif overflow-x-hidden">
      <ToastContainer />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial="hidden"
        animate={heroControls}
        variants={fadeIn}
        className="relative h-[80vh] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={Image1}
            alt="Dubai Panjshir EmeraldVault Mining"
            className="w-full h-full object-cover brightness-75"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-900/70 to-emerald-950/80" />
        </div>

        <div className="container relative z-10 px-4 sm:px-6 max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center text-emerald-300 hover:text-emerald-100 transition-colors mb-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block mb-4 px-4 py-1 bg-emerald-700/30 backdrop-blur-sm rounded-full border border-emerald-500/30"
          >
            <span className="text-emerald-200 font-sans text-sm tracking-wider uppercase">Panjshir Valley Mining</span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 tracking-tight text-white"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Dubai Panjshir EmeraldVault <span className="text-emerald-300">Mining</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl max-w-2xl mb-8 text-emerald-50/90 leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Expert mining services for Panjshir emeralds, blending tradition and innovation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a href="#services">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500/50 shadow-lg">
                Explore Services
              </button>
            </a>
            <a href="#contact">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 border border-emerald-400/50 text-white hover:bg-emerald-800/30 backdrop-blur-sm">
                Contact Us
              </button>
            </a>
          </motion.div>
        </div>
      </motion.section>

      {/* Video Introduction Section */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-b from-green-900 to-teal-800">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <div className="inline-block px-4 py-1 bg-emerald-800 rounded-full text-emerald-200 font-sans text-sm tracking-wider uppercase mb-4">
                Our Methods
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Sustainable Mining</h2>
              <p className="text-emerald-100 text-lg mb-8 leading-relaxed">
                We honor the Panjshir Valley’s legacy with sustainable mining practices.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Low-impact extraction</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Ethical support for workers</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Traceable emeralds</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Ecosystem restoration</p>
                </div>
              </div>

              <a href="#sustainability">
                <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500/50 shadow-lg">
                  Learn More
                </button>
              </a>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-w-16 aspect-h-9 relative">
                <video
                  ref={videoRef}
                  className="w-full h-[600px] object-cover"
                  controls={isPlaying}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source src={Video1} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>

                {!isPlaying && (
                  <button
                    onClick={toggleVideo}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                  >
                    <div className="w-20 h-20 rounded-full bg-emerald-600 flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent ml-2" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-4 sm:px-6">
        <motion.div
          ref={servicesRef}
          initial="hidden"
          animate={servicesControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-50 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4"
            >
              Our Expertise
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Mining Services
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Tailored services for Panjshir emerald mining.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="space-y-4">
                {miningServices.map((service) => (
                  <motion.div
                    key={service.id}
                    className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                      activeService === service.id
                        ? "bg-emerald-700 text-white shadow-lg"
                        : "bg-emerald-50 text-gray-800 hover:bg-emerald-100"
                    }`}
                    onClick={() => setActiveService(service.id)}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-start">
                      <div className={`mr-4 ${activeService === service.id ? "text-emerald-200" : "text-emerald-700"}`}>
                        {service.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                        <p className={activeService === service.id ? "text-emerald-100" : "text-gray-600"}>
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              {miningServices.map((service) => (
                <motion.div
                  key={service.id}
                  className={`bg-white rounded-2xl shadow-xl overflow-hidden ${
                    activeService === service.id ? "block" : "hidden"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: activeService === service.id ? 1 : 0, y: activeService === service.id ? 0 : 20 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="relative h-80">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <h3 className="text-3xl font-bold text-white mb-2">{service.title}</h3>
                      <p className="text-emerald-100 text-lg">{service.description}</p>
                    </div>
                  </div>

                  <div className="p-8">
                    <h4 className="text-xl font-semibold mb-4 text-emerald-800">What We Provide:</h4>
                    <ul className="space-y-3">
                      {service.details.map((detail, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-6 w-6 text-emerald-600 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8">
                      <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg">
                        Request This Service <ArrowRight className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Mining Process Section */}
      <MiningProcessSection />

      {/* Sustainability Section */}
      <section id="sustainability" className="py-24 px-4 sm:px-6 bg-gradient-to-b from-emerald-50 to-white">
        <motion.div
          ref={sustainabilityRef}
          initial="hidden"
          animate={sustainabilityControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-100 rounded-full text-emerald-800 font-sans text-sm tracking-wider uppercase mb-4"
            >
              Our Pledge
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Sustainable Mining
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Committed to ethical mining in the Panjshir Valley.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeInRight} className="order-2 md:order-1">
              <div className="space-y-8">
                {sustainabilityPractices.map((practice) => (
                  <motion.div
                    key={practice.id}
                    className="flex items-start bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-emerald-100"
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  >
                    <div className="flex-shrink-0 mr-5 bg-emerald-50 p-3 rounded-full">{practice.icon}</div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-emerald-800">{practice.title}</h3>
                      <p className="text-gray-600">{practice.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeInLeft} className="relative order-1 md:order-2">
              <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={Image8}
                  alt="Sustainable Mining"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 to-transparent" />
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">Panjshir Stewardship</h3>
                  <p className="text-emerald-100">Prioritizing the valley’s environment and people.</p>
                </div>
              </div>

              <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-emerald-100 rounded-2xl -z-10" />
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-emerald-200 rounded-2xl -z-10" />

              <motion.div
                className="absolute top-10 -right-10 bg-white p-4 rounded-xl shadow-xl"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Leaf className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">100% Ethical</p>
                    <p className="text-sm text-gray-600">Panjshir Practices</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute bottom-20 -left-10 bg-white p-4 rounded-xl shadow-xl"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Valley First</p>
                    <p className="text-sm text-gray-600">Supporting locals</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stake Holding Section */}
      <section id="stakeholding" className="py-24 px-4 sm:px-6 bg-white text-gray-900">
        <motion.div
          ref={stakeHoldingRef}
          initial="hidden"
          animate={stakeHoldingControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="text-center mb-16">
            <motion.div
              variants={fadeIn}
              className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full font-sans text-sm tracking-wider uppercase mb-4"
            >
              Your Path to Wealth
            </motion.div>
            <motion.h2 variants={fadeIn} className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Invest in Mining
            </motion.h2>
            <motion.p variants={fadeIn} className="max-w-2xl mx-auto text-gray-600 text-lg">
              Seize the opportunity to stake your claim in Panjshir emeralds.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              variants={fadeIn}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="relative group"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10" />
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Gem className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Lucrative Returns</h3>
                <p className="text-gray-600">Invest in emeralds with proven value growth.</p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl" />
              </div>
            </motion.div>

            <motion.div
              variants={fadeIn}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="relative group"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10" />
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Secure Investment</h3>
                <p className="text-gray-600">Backed by certified emeralds in secure vaults.</p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl" />
              </div>
            </motion.div>

            <motion.div
              variants={fadeIn}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className="relative group"
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 transform group-hover:scale-[1.03] transition-transform duration-300 -z-10" />
              <div className="relative p-8 rounded-2xl bg-white border border-emerald-200 shadow-lg h-full">
                <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-3 rounded-xl inline-block mb-6 shadow-md">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">VIP Privileges</h3>
                <p className="text-gray-600">Exclusive access to auctions and gem allocations.</p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-emerald-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-2xl" />
              </div>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeIn} className="mt-16 text-center">
            <Link to="/pricing-plans">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-12 px-10 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg transform hover:scale-105 transition-all duration-300">
                Secure Your Stake Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      

      {/* Gallery Section */}
      <GallerySection />
      
      {/* Video Introduction Section (Duplicate) */}
      <section className="py-24 px-4 sm:px-6 bg-gradient-to-b from-green-900 to-teal-800">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <div className="inline-block px-4 py-1 bg-emerald-800 rounded-full text-emerald-200 font-sans text-sm tracking-wider uppercase mb-4">
                Our Heritage
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Panjshir Legacy</h2>
              <p className="text-emerald-100 text-lg mb-8 leading-relaxed">
              Discover the rich history and cultural significance of Panjshir Valley emerald mining,
              where ancient traditions meet modern expertise.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Centuries of mining heritage</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Traditional Afghan craftsmanship</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">World-renowned emerald quality</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-emerald-100">Cultural preservation efforts</p>
                </div>
              </div>

              <a href="#sustainability">
                <button className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500/50 shadow-lg">
                  Learn More
                </button>
              </a>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-w-16 aspect-h-9 relative">
                <video
                  ref={videoRef}
                  className="w-full h-[600px] object-cover"
                  controls={isPlaying}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source src={video3} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>

                {!isPlaying && (
                  <button
                    onClick={toggleVideo}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                  >
                    <div className="w-20 h-20 rounded-full bg-emerald-600 flex items-center justify-center">
                      <div className="w-0 h-0 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent ml-2" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 sm:px-6 bg-gradient-to-r from-emerald-900 to-green-900 text-white">
        <motion.div
          ref={contactRef}
          initial="hidden"
          animate={contactControls}
          variants={fadeIn}
          className="container mx-auto px-4 sm:px-6 max-w-7xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1 bg-emerald-800 rounded-full text-emerald-200 font-sans text-sm tracking-wider uppercase mb-4">
                Reach Out
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Contact Our Experts</h2>
              <p className="text-emerald-100 text-lg mb-8">
                Questions about Panjshir emerald mining? We`re here to assist.
              </p>

              <div className="space-y-6 mb-8">
                <a
                  href="tel:0202201616"
                  className="flex items-start bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 p-6 hover:transform hover:scale-105 transition-transform duration-300"
                >
                  <div className="h-12 w-12 rounded-full bg-emerald-800 flex items-center justify-center mr-4">
                    <Phone className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Call Us</h3>
                    <p className="text-emerald-100">+93 72 959 9599</p>
                    <p className="text-sm text-emerald-300 mt-1">Available 24/7</p>
                  </div>
                </a>

                <div className="flex items-start bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 p-6 hover:transform hover:scale-105 transition-transform duration-300">
                  <div className="h-12 w-12 rounded-full bg-emerald-800 flex items-center justify-center mr-4">
                    <Mail className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Email Us</h3>
                    <p className="text-emerald-100">info@panjshiremeraldvault.com</p>
                    <p className="text-sm text-emerald-300 mt-1">Response within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start bg-emerald-800/50 backdrop-blur-sm rounded-2xl border border-emerald-700/50 p-6 hover:transform hover:scale-105 transition-transform duration-300">
                  <div className="h-12 w-12 rounded-full bg-emerald-800 flex items-center justify-center mr-4">
                    <Clock className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Schedule a Consultation</h3>
                    <p className="text-emerald-100">Book a call with our mining team.</p>
                    <p className="text-sm text-emerald-300 mt-1">Virtual or on-site options.</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white rounded-2xl shadow-2xl p-8 text-gray-900">
                <h3 className="text-2xl font-bold mb-6 text-emerald-800">Send Us a Message</h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-md border ${
                          formErrors.name ? "border-red-500" : "border-gray-300"
                        } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                        placeholder="John Doe"
                        disabled={isSubmitting}
                      />
                      {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-md border ${
                          formErrors.email ? "border-red-500" : "border-gray-300"
                        } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                        placeholder="john@dubaipanjshiremeraldvault.com"
                        disabled={isSubmitting}
                      />
                      {formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                      Service of Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      disabled={isSubmitting}
                    >
                      <option value="">Select a service</option>
                      <option value="exploration">Exploration & Surveying</option>
                      <option value="extraction">Extraction Operations</option>
                      <option value="processing">Processing & Sorting</option>
                      <option value="consulting">Mining Consulting</option>
                      <option value="stakeholding">Stake Holding Opportunities</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-md border ${
                        formErrors.message ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                      placeholder="Tell us about your inquiry..."
                      disabled={isSubmitting}
                    />
                    {formErrors.message && <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors h-11 px-8 ${
                      isSubmitting
                        ? "bg-emerald-400 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    } text-white shadow-lg`}
                  >
                    {isSubmitting ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="mr-2"
                      >
                        <RefreshCw size={16} />
                      </motion.div>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}